/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */

import React from "react";
import { Provider } from "react-redux";
import { CookiesProvider, Cookies } from "react-cookie";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { BrowserRouter } from "react-router-dom";
import store from "./src/redux/store";
import createEmotionCache from "./src/utils/createEmotionCache";
import { ThemeBodySync } from "./src/components/Theme/ThemeBodySync";

// Default theme - you can import your custom theme here later
import { createTheme } from "@mui/material/styles";
const theme = createTheme();

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

export const wrapRootElement = ({ element }) => {
  // For SSR support with react-cookie v8 - initialize cookies for server-side rendering
  const cookies = typeof window !== "undefined" ? undefined : new Cookies();

  return (
    <Provider store={store}>
      <>
        <ThemeBodySync />
        <CookiesProvider cookies={cookies}>
          <CacheProvider value={clientSideEmotionCache}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <BrowserRouter>{element}</BrowserRouter>
            </ThemeProvider>
          </CacheProvider>
        </CookiesProvider>
      </>
    </Provider>
  );
};

// Ładowanie konfiguracji reCAPTCHA
export const onClientEntry = () => {
  if (typeof window !== "undefined") {
    // Sprawdź czy konfiguracja już istnieje
    if (!window.RUNTIME_CONFIG) {
      // Załaduj config.js
      const script = document.createElement("script");
      script.src = "/config.js";
      script.async = true;
      script.onload = () => {
        console.log("reCAPTCHA configuration loaded successfully");
      };
      script.onerror = () => {
        console.error("Failed to load reCAPTCHA configuration");
      };
      document.head.appendChild(script);
    }

    // Wyłącz automatyczne tłumaczenie przeglądarki
    const disableTranslation = () => {
      // Ustaw atrybuty na głównych elementach
      if (document.documentElement) {
        document.documentElement.setAttribute("translate", "no");
        document.documentElement.classList.add("notranslate");
      }
      if (document.body) {
        document.body.setAttribute("translate", "no");
        document.body.classList.add("notranslate");
      }

      // Usuń klasy związane z tłumaczeniem (Chrome dodaje klasy jak "translated-ltr", "translated-rtl")
      const removeTranslationClasses = () => {
        const elements = document.querySelectorAll("*");
        elements.forEach((el) => {
          if (
            el.classList.contains("translated-ltr") ||
            el.classList.contains("translated-rtl")
          ) {
            el.classList.remove("translated-ltr", "translated-rtl");
          }
          // Ustaw atrybut translate="no" na wszystkich elementach
          if (!el.hasAttribute("translate")) {
            el.setAttribute("translate", "no");
          }
        });
      };

      // Usuń klasy tłumaczenia natychmiast
      removeTranslationClasses();

      // Monitoruj zmiany w DOM i usuń klasy tłumaczenia
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class"
          ) {
            const target = mutation.target;
            if (
              target instanceof Element &&
              (target.classList.contains("translated-ltr") ||
                target.classList.contains("translated-rtl"))
            ) {
              target.classList.remove("translated-ltr", "translated-rtl");
              target.setAttribute("translate", "no");
            }
          }
          if (mutation.type === "childList") {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof Element) {
                node.setAttribute("translate", "no");
                if (
                  node.classList.contains("translated-ltr") ||
                  node.classList.contains("translated-rtl")
                ) {
                  node.classList.remove("translated-ltr", "translated-rtl");
                }
              }
            });
          }
        });
        removeTranslationClasses();
      });

      // Rozpocznij obserwację zmian w DOM
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      // Usuń klasy tłumaczenia co sekundę (dodatkowa ochrona)
      const intervalId = setInterval(() => {
        removeTranslationClasses();
      }, 1000);

      // Wyczyść interval po 30 sekundach (nie potrzebujemy go dłużej)
      setTimeout(() => {
        clearInterval(intervalId);
      }, 30000);
    };

    // Uruchom natychmiast
    disableTranslation();

    // Uruchom również po załadowaniu DOM
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", disableTranslation);
    } else {
      disableTranslation();
    }

    // Uruchom również po pełnym załadowaniu strony
    window.addEventListener("load", disableTranslation);
  }
};